/**
 * Rebuilds the nested, slug-keyed map the app still expects from the flat shape.
 * Temporary: every legacy quirk lives here, marked `legacy:`, and dies with it.
 */

import { CURRICULUM_ENTITY_TYPES as E } from '../../model/curriculumEntityTypes';
import { CURRICULUM_TYPENAMES as T } from '../../model/curriculumTypenames';

const TYPENAME_BY_ENTITY_TYPE = {
  [E.FOLDER]: T.FOLDER,
  [E.COLLECTION]: T.COLLECTION,
  [E.SET]: T.SET,
  [E.ACTIVITY]: T.ACTIVITY,
};

/** legacy: a child is labelled by the field it hung off its parent, not by what it is. */
const NODE_TYPE_BY_PARENT_ENTITY_TYPE = {
  [E.FOLDER]: 'folder',
  [E.COLLECTION]: 'set',
  [E.SET]: 'activity',
};

/** The field each kind of node used to carry its raw children under. */
const CHILDREN_FIELD_BY_ENTITY_TYPE = {
  [E.FOLDER]: 'content',
  [E.COLLECTION]: 'sets',
  [E.SET]: 'activities',
};

const PUBLIC = 'Public';

const typeRef = (name, colorTheme) =>
  name == null && colorTheme == null
    ? null
    : { __typename: T.NODE_TYPE, name, colorTheme };

const certificateRef = (name, certificateCode) =>
  name == null && certificateCode == null
    ? null
    : { __typename: T.CERTIFICATE, name, certificateCode };

const buildContentSectionGroup = (group, questionsIds, sectionId) => ({
  __typename: T.CONTENT_SECTION_GROUP,
  id: group.groupId,
  name: group.name,
  requiredGrading: group.requiredGrading,
  questionsIds,
  total: questionsIds.length,
  current: questionsIds.indexOf(sectionId),
});

const buildSection = (section, group, questionsIds) => {
  const base = {
    __typename: section.typename,
    id: section.id,
    // legacy: the shared resolver read `contentType` off the entry root instead of
    // `entry.sys`, so every section came back as 'contentSection'.
    type: 'contentSection',
  };

  // The old query asked for different fields per member of the union, so a quiz
  // question carried no `heading` and a content section carried no `answers`.
  if (section.typename === T.QUIZ_QUESTION) {
    return {
      ...base,
      name: section.name,
      header: section.header,
      description: section.description,
      isOptionalQuiz: section.isOptionalQuiz,
      answers: section.answers
        ? section.answers.map((answer) => ({ ...answer, __typename: T.QUIZ_ANSWER }))
        : null,
    };
  }

  if (section.typename !== T.CONTENT_SECTION) {
    // Surveys had no fragment of their own and arrived with nothing but an id.
    return base;
  }

  return {
    ...base,
    heading: section.heading,
    subheading: section.subheading,
    content: section.content,
    facilitatorNote: section.facilitatorNote,
    visibleInPageNavigation: section.visibleInPageNavigation,
    onlyVisibleToFacilitators: section.onlyVisibleToFacilitators,
    isOpenTextAnswer: section.isOpenTextAnswer,
    requiredGrading: section.requiredGrading,
    isOptionalTextAnswer: section.isOptionalTextAnswer,
    isIdeProjectHideBlockPalette: section.isIdeProjectHideBlockPalette,
    contentSectionGroup: group
      ? buildContentSectionGroup(group, questionsIds, section.id)
      : null,
  };
};

/** Sections of one placement; group lists are per placement and keyed by name. */
const buildSections = (node, sectionsById, groupsById) => {
  if (!node.hasContentSections) {
    return null;
  }

  const sections = node.sectionIds
    .map((sectionId) => sectionsById[sectionId])
    .filter(Boolean);

  const questionsIdsByGroupName = {};

  for (const section of sections) {
    const group = section.groupId ? groupsById[section.groupId] : null;

    if (group) {
      const ids = questionsIdsByGroupName[group.name] || [];

      ids.push(section.id);
      questionsIdsByGroupName[group.name] = ids;
    }
  }

  return sections.map((section) => {
    const group = section.groupId ? groupsById[section.groupId] : null;

    return buildSection(
      section,
      group,
      group ? questionsIdsByGroupName[group.name] : [],
    );
  });
};

const COLLECTION_CHILD_NODE_TYPE = NODE_TYPE_BY_PARENT_ENTITY_TYPE[E.COLLECTION];

/** The Contentful content type id each kind of node was published under. */
const CONTENT_TYPE_ID_BY_ENTITY_TYPE = {
  [E.SET]: 'set',
  [E.ACTIVITY]: 'activity',
};

const buildNode = (node, nodeType) => {
  const common = {
    __typename: TYPENAME_BY_ENTITY_TYPE[node.entityType] || null,
    // The shape's own vocabulary, carried through: `nodeType` names a node by
    // the field it hung off its parent, which is not the same thing.
    entityType: node.entityType,
    id: node.entityId,
    name: node.name,
    slug: node.slug,
    permissionLevel: node.permissionLevel,
    nodeType,
    path: node.path,
  };

  // legacy: everything under a collection was resolved through the activity set
  // type, whose `type` is the Contentful content type id - so an activity placed
  // there reads 'activity'. Code branching on `type` still relies on that.
  if (nodeType === COLLECTION_CHILD_NODE_TYPE) {
    common.type = CONTENT_TYPE_ID_BY_ENTITY_TYPE[node.entityType] ?? null;
    // `ActivitySet.setType` fell back to `activityType`, so a card there took its
    // label and colour off `setType` whichever kind of entry was linked.
    common.setType =
      typeRef(node.setTypeName, node.setTypeColorTheme) ??
      typeRef(node.activityTypeName, node.activityTypeColorTheme);
  }

  switch (node.entityType) {
    case E.FOLDER:
      return {
        ...common,
        folderType: typeRef(node.folderTypeName, node.folderTypeColorTheme),
        coverImage: node.coverImage,
        welcomeModal: node.welcomeModal,
      };
    case E.COLLECTION:
      return {
        ...common,
        invisibleCollection: node.invisibleCollection,
        certificateType: certificateRef(node.certificateTypeName, node.certificateCode),
        endsInCertificate: node.endsInCertificate,
        program: node.program,
        icon: node.icon,
        collectionType: typeRef(node.collectionTypeName, node.collectionTypeColorTheme),
        welcomeModal: node.welcomeModal,
      };
    case E.SET:
      return {
        ...common,
        isOptional: node.isOptional,
        shortDescription: node.shortDescription,
        introduction: node.introduction,
        isBlockBasedIdeProject: node.isBlockBasedIdeProject,
        ideProjectType: node.ideProjectType,
        ideType: node.ideType,
        ideProjectStarterCode: node.ideProjectStarterCode,
        goals: node.goals,
        estimatedTimeInMinutes: node.estimatedTimeInMinutes,
        tags: node.tags,
        externalUrl: node.externalUrl,
        easeOfFacilitation: node.easeOfFacilitation,
        welcomeModal: node.welcomeModal,
        setType: typeRef(node.setTypeName, node.setTypeColorTheme),
        icon: node.icon,
      };
    case E.ACTIVITY:
      return {
        ...common,
        shortDescription: node.shortDescription,
        estimatedTimeInMinutes: node.estimatedTimeInMinutes,
        tags: node.tags,
        externalUrl: node.externalUrl,
        isOptional: node.isOptional,
        easeOfFacilitation: node.easeOfFacilitation,
        activityType: typeRef(node.activityTypeName, node.activityTypeColorTheme),
        icon: node.icon,
      };
    default:
      return common;
  }
};

/**
 * One nested tree. `studentView` keeps only public nodes; unfiltered children
 * stay on the parent under their original field name.
 */
const buildTree = (shape, studentView) => {
  const nodesByPath = {};
  const sectionsById = {};
  const groupsById = {};

  for (const node of shape.nodes) {
    nodesByPath[node.path] = node;
  }
  for (const section of shape.sections) {
    sectionsById[section.id] = section;
  }
  for (const group of shape.groupSections) {
    groupsById[group.groupId] = group;
  }

  const build = (path, parentEntityType, inheritedCoverImage, ancestry = {}) => {
    const node = nodesByPath[path];

    if (!node) {
      return null;
    }

    const built = buildNode(node, NODE_TYPE_BY_PARENT_ENTITY_TYPE[parentEntityType] || null);

    // Where this node hangs, by the ids of its ancestors and of itself. Answers
    // and quiz attempts are stored under exactly these ids, so the reader must
    // not compose them out of paths or chains: one rule, applied here.
    const placement = {
      folderId: node.entityType === E.FOLDER ? node.entityId : ancestry.folderId ?? null,
      collectionId:
        node.entityType === E.COLLECTION ? node.entityId : ancestry.collectionId ?? null,
      setId: node.entityType === E.SET ? node.entityId : ancestry.setId ?? null,
    };

    built.placement = placement;

    // legacy: a collection sitting directly inside a folder borrows the folder's
    // cover image when it has no icon of its own.
    if (inheritedCoverImage && !built.icon) {
      built.icon = { ...inheritedCoverImage };
    }

    // Only sets and activities ever asked for content sections, so only they
    // carry the key - null when the entity has none, as the old query returned.
    if (node.entityType === E.SET || node.entityType === E.ACTIVITY) {
      built.contentSections = buildSections(node, sectionsById, groupsById);
    }

    const coverImageForChildren =
      node.entityType === E.FOLDER ? node.coverImage : null;

    const children = node.childPaths
      .map((childPath) =>
        build(childPath, node.entityType, coverImageForChildren, placement),
      )
      .filter(Boolean);

    const childrenField = CHILDREN_FIELD_BY_ENTITY_TYPE[node.entityType];

    if (childrenField) {
      built[childrenField] = children;
    }

    built.nodes = {};
    for (const child of children) {
      if (!studentView || child.permissionLevel === PUBLIC) {
        built.nodes[child.slug] = child;
      }
    }

    return built;
  };

  const roots = shape.nodes
    .filter((node) => !node.parentPath)
    .map((node) => build(node.path, null, null, {}))
    .filter(Boolean);

  const result = {};

  for (const root of roots) {
    if (!studentView || root.permissionLevel === PUBLIC) {
      result[root.slug] = root;
    }
  }

  return result;
};

/** The `{ code, welcomeModal, nodes: { curriculums, studentCurriculums } }` payload. */
export const adaptCurriculumShape = (shape) => {
  if (!shape) {
    return null;
  }

  return {
    code: shape.code,
    welcomeModal: shape.welcomeModal,
    nodes: {
      curriculums: buildTree(shape, false),
      studentCurriculums: buildTree(shape, true),
    },
  };
};

